import { type FC } from "react";

import { CalendarPlus, Check, Search } from "lucide-react";
import { format } from "date-fns";

import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useAppContext } from "@/shared/hooks/useAppContext";

import type { AuthorityVerificationConfig } from "@/shared/constants/authorityVerification";

import assetSearchHero from "@/shared/assets/illustrations/asset-search-hero.png";
import assetSearchProgress from "@/shared/assets/illustrations/asset-search-progress.png";
import irsLogo from "@/shared/assets/illustrations/irs-logo-transparent.png";
import equifaxLogo from "@/shared/assets/illustrations/equifax-logo.svg";

export const AuthorityCopySkeleton: FC = () => (
  <div className="flex w-full flex-col gap-2 py-1">
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

const HeroBannerSkeleton: FC = () => (
  <div className="bg-white border border-primary rounded-xl overflow-hidden flex lg:h-[276px] lg:border-r-0">
    <div className="flex flex-col gap-4 shrink-0 w-full lg:w-[480px] p-5 sm:p-8 justify-center">
      <Skeleton className="h-6 w-40 rounded-full" />
      <Skeleton className="h-7 w-72" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <Skeleton className="h-10 w-36 rounded-md" />
    </div>
    <div className="hidden lg:flex flex-1 min-w-0 items-center justify-end overflow-hidden">
      <Skeleton className="h-full w-full" />
    </div>
  </div>
);

function downloadCalendarInvite(start: Date, durationMinutes = 30) {
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const now = fmt(new Date());
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Elayne//Asset Search//EN",
    "BEGIN:VEVENT",
    `UID:${now}-${crypto.randomUUID()}@elayne.com`,
    `DTSTAMP:${now}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(new Date(start.getTime() + durationMinutes * 60 * 1000))}`,
    "SUMMARY:Elayne Asset Search Call",
    "DESCRIPTION:Your Estate Expert will start the searches and walk you through the forms.",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "elayne-asset-search-call.ics";
  a.click();
  URL.revokeObjectURL(url);
}

const SourceLogosBar: FC = () => (
  <div className="flex flex-wrap items-center gap-3 sm:gap-5 bg-background border border-t-0 border-primary rounded-b-xl px-4 py-3">
    <span className="text-sm text-secondary">Records come from</span>
    <div className="flex items-center gap-3 sm:gap-5">
      <img src={irsLogo} alt="IRS" className="h-4 object-contain" />
      <img src={equifaxLogo} alt="Equifax" className="h-4 object-contain relative top-[1px]" />
      <span className="text-sm text-secondary">and 100+ other sources</span>
    </div>
  </div>
);

interface HeroBannerProps {
  isAuthorized: boolean;
  isPaidUser: boolean;
  isSearching: boolean;
  isLoading: boolean;
  searchStartedAt?: string | null;
  config: AuthorityVerificationConfig;
  isConfigReady: boolean;
  simplifiedFlow: boolean;
  callBookedAt?: string | null;
  onStartSearch: () => void;
  onReschedule: () => void;
}

export const HeroBanner: FC<HeroBannerProps> = ({
  isAuthorized,
  isPaidUser,
  isSearching,
  isLoading,
  searchStartedAt,
  config,
  isConfigReady,
  simplifiedFlow,
  callBookedAt,
  onStartSearch,
  onReschedule,
}) => {
  const { person } = useAppContext();
  const firstName = person?.first_name ?? "your loved one";

  if (isLoading) return <HeroBannerSkeleton />;

  if (isSearching) {
    const startDate = searchStartedAt ? format(new Date(searchStartedAt), "MMMM d, yyyy") : null;
    const EIGHT_WEEKS_MS = 8 * 7 * 24 * 60 * 60 * 1000;
    const MAX_PERCENT = 95;
    const elapsedMs = searchStartedAt ? Date.now() - new Date(searchStartedAt).getTime() : 0;
    const MIN_PERCENT = 5;
    const progressPercent = Math.max(
      MIN_PERCENT,
      Math.min(Math.round((elapsedMs / EIGHT_WEEKS_MS) * MAX_PERCENT), MAX_PERCENT),
    );

    return (
      <div className="bg-white border border-primary rounded-xl overflow-hidden flex lg:h-[276px] lg:border-r-0">
        <div className="flex flex-col gap-4 shrink-0 w-full lg:w-[480px] p-5 sm:p-8 justify-center">
          <div className="flex flex-col gap-3 items-start">
            <Tag color="green" icon={Search}>
              Search in progress
            </Tag>
            <h4>The search for {firstName}'s assets is underway</h4>
            <p className="text-sm text-secondary">
              Elayne Asset Search is checking 100+ financial sources, including 10 years of IRS
              records. You'll get an email and notification when something is found.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {startDate && <p className="text-sm text-secondary">Started on {startDate}</p>}
            <div className="h-2 w-full rounded-full bg-subtle">
              <div
                className="h-2 rounded-full bg-brand transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="hidden lg:flex flex-1 min-w-0 items-center justify-end overflow-hidden">
          <img
            src={assetSearchProgress}
            alt="Asset search in progress"
            className="h-[135%] w-auto"
          />
        </div>
      </div>
    );
  }

  if (simplifiedFlow && callBookedAt) {
    const isValidDate = !isNaN(new Date(callBookedAt).getTime());
    const callDate = isValidDate ? new Date(callBookedAt) : null;
    const formattedCall = callDate ? format(callDate, "EEEE, M/d 'at' h:mm a zzz") : null;

    return (
      <div className="flex flex-col">
        <div className="bg-white border border-primary rounded-t-xl overflow-hidden flex lg:border-r-0 lg:h-[304px]">
          <div className="flex flex-col gap-4 shrink-0 w-full lg:w-[480px] p-5 sm:p-8 justify-center">
            <div className="flex flex-col gap-3 items-start">
              <span className="flex items-center gap-1 text-xs font-medium text-brand">
                <Check className="h-3 w-3" />
                Your call is booked
              </span>
              <h4>{formattedCall ? `Your call is ${formattedCall}` : "Your call is booked"}</h4>
              <p className="text-sm text-secondary">
                On the call, your Estate Expert starts the searches that can begin today
                and walks you through the forms that let us request records for you. Before
                the call, gather what you have, like an ID and the death certificate.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {callDate && (
                <Button variant="outline" size="md" onClick={() => downloadCalendarInvite(callDate)}>
                  <CalendarPlus className="h-4 w-4" />
                  Add to calendar
                </Button>
              )}
              <button
                onClick={onReschedule}
                className="text-sm font-medium text-secondary underline"
              >
                Reschedule
              </button>
            </div>
          </div>
          <div className="hidden lg:flex flex-1 min-w-0 items-center justify-end overflow-hidden">
            <img src={assetSearchHero} alt="Elayne Asset Search" className="h-[120%] w-auto -translate-y-[8%]" />
          </div>
        </div>
        <SourceLogosBar />
      </div>
    );
  }

  const heroCopy = simplifiedFlow ? (
    isPaidUser ? (
      <div className="flex flex-col gap-3 items-start">
        <span className="flex items-center gap-1.5 text-sm font-medium text-brand">
          <Check className="h-4 w-4" />
          Elayne Asset Search is paid for
        </span>
        <h4>Book a call to start your search</h4>
        <p className="text-sm text-secondary">
          On the call, your Estate Expert starts the searches that can begin today
          and walks you through the forms that let us request records for you.
        </p>
      </div>
    ) : (
      <div className="flex flex-col gap-3 items-start">
        <Tag color="green" icon={Search}>
          Elayne Asset Search
        </Tag>
        <h4>Find all the accounts {firstName} left behind</h4>
        <p className="text-sm text-secondary">
          Get a list of {firstName}'s accounts, policies, and debts, found with IRS
          authorization, their Equifax credit report, life insurance locators, and 100+
          other sources.
        </p>
      </div>
    )
  ) : isAuthorized && isPaidUser ? (
    <div className="flex flex-col gap-3 items-start">
      <Tag color="green" icon={Check}>
        Your search is paid for
      </Tag>
      <h4>Find what {firstName}'s estate is owed</h4>
      <p className="text-sm text-secondary">
        Elayne Asset Search checks the sources below for accounts, policies, and property in
        the estate's name. Most families find something they didn't know about
      </p>
      <p className="text-sm text-secondary">
        A signature and a quick ID check start your search
      </p>
    </div>
  ) : isAuthorized ? (
    <div className="flex flex-col gap-3 items-start">
      <Tag color="green" icon={Check}>
        Your authority is confirmed
      </Tag>
      <h4>Find what {firstName}'s estate is owed</h4>
      <p className="text-sm text-secondary">
        Elayne Asset Search checks the sources below for accounts, policies, and property in
        the estate's name. Most families find something they didn't know about
      </p>
    </div>
  ) : (
    <div className="flex flex-col gap-3 items-start">
      <Tag color="green" icon={Check}>
        Confirm your legal authority
      </Tag>
      <h4>One step to unlock Elayne Asset Search</h4>
      {isConfigReady ? (
        <p className="text-sm text-secondary">
          Banks and insurers will only release {firstName}'s records to someone with legal
          authority over the estate. Your {config.documentName} ({config.documentDescription})
          is the one document needed.
        </p>
      ) : (
        <AuthorityCopySkeleton />
      )}
    </div>
  );

  const buttonText = simplifiedFlow
    ? (isPaidUser ? "Book a call" : "Start search")
    : (isAuthorized && isPaidUser
        ? "Start search"
        : isAuthorized
          ? "Unlock asset search"
          : "Add document");

  return (
    <div className="flex flex-col">
      <div className="bg-white border border-primary rounded-t-xl overflow-hidden flex lg:border-r-0 lg:h-[276px]">
        <div className="flex flex-col gap-4 shrink-0 w-full lg:w-[480px] p-5 sm:p-8 justify-center">
          {heroCopy}
          {!isSearching && (
            <div>
              <Button variant="brand" size="md" onClick={onStartSearch}>
                {buttonText}
              </Button>
            </div>
          )}
        </div>

        <div className="hidden lg:flex flex-1 min-w-0 items-center justify-end overflow-hidden">
          <img src={assetSearchHero} alt="Elayne Asset Search" className="h-[120%] w-auto -translate-y-[8%]" />
        </div>
      </div>

      {simplifiedFlow && <SourceLogosBar />}
    </div>
  );
};
